import {
  DemandAdjustmentStatus,
  DemandAdjustmentTargets,
  DemandAdjustmentType,
} from "../../types/demand-factor.type";
import { formatThaiShortDate } from "../../utils/date";

export const DEMAND_FACTOR_PATH = "/demand-factors";

export const API_DATE_FORMAT = "YYYY-MM-DD";

/** Sentinel a filter select uses for "no filter"; never sent to the API. */
export const ALL = "all";

/** The API's per-row multiplier bounds (the combined multiplier across rows may exceed them). */
export const MIN_MULTIPLIER = 0;
export const MAX_MULTIPLIER = 2;
export const MULTIPLIER_STEP = 0.01;

/** Mirrors the API's `name` length limit. */
export const MAX_NAME_LENGTH = 100;

/**
 * `customer_groups.prefix` of ลูกค้าทั่วไป (group Z). Every other group is a named chain —
 * "เชนทั้งหมด" (the legacy MODERN_TRADE label) is every group but this one (R5).
 */
export const GENERAL_CUSTOMER_GROUP_PREFIX = "Z";

export const TYPE_LABEL: Record<DemandAdjustmentType, string> = {
  event: "เทศกาล/อีเวนต์",
  season: "ฤดูกาล",
  promo: "โปรโมชัน",
  growth: "การเติบโต",
};

export const TYPE_OPTIONS: DemandAdjustmentType[] = ["event", "season", "promo", "growth"];

export const STATUS_LABEL: Record<DemandAdjustmentStatus, string> = {
  active: "ใช้อยู่",
  upcoming: "ล่วงหน้า",
  expired: "หมดอายุ",
};

export const STATUS_OPTIONS: DemandAdjustmentStatus[] = ["active", "upcoming", "expired"];

export const STATUS_COLOR: Record<DemandAdjustmentStatus, "success" | "info" | "default"> = {
  active: "success",
  upcoming: "info",
  expired: "default",
};

export const TYPE_DISABLED_LABEL = "ปิดอยู่ ไม่ถูกคำนวณ";

/** Shown for a legacy row saved without a name. */
export const UNNAMED_ROW = "(ไม่มีชื่อ)";

export const EMPTY_TARGETS: DemandAdjustmentTargets = {
  target_branch: [],
  target_route: [],
  target_customer_group: [],
  target_channel: [],
  exclude_channel: [],
  target_product_code: [],
};

/** Decimal places a multiplier is shown with before trailing zeros are dropped. */
const MULTIPLIER_DISPLAY_DECIMALS = 4;

/** `1.5` → "×1.5", `1.3500000000000003` → "×1.35", null → "-". */
export const formatMultiplier = (multiplier: number | null): string =>
  multiplier === null
    ? "-"
    : `×${Number(multiplier.toFixed(MULTIPLIER_DISPLAY_DECIMALS))}`;

/** How many names a one-line summary spells out before collapsing the rest into "+N". */
const MAX_LISTED_LABELS = 3;

/** ["A", "B", "C", "D", "E"] → "A, B, C +2" — a chain list can run to thirty groups. */
export const joinLabels = (labels: string[]): string => {
  const listed = labels.slice(0, MAX_LISTED_LABELS).join(", ");
  const hiddenCount = labels.length - MAX_LISTED_LABELS;
  return hiddenCount > 0 ? `${listed} +${hiddenCount}` : listed;
};

/** Display names for codes, falling back to the code itself when the lookup lacks it. */
export const labelsOf = (codes: string[], nameByCode: Map<string, string>): string[] =>
  codes.map((code) => nameByCode.get(code) ?? code);

/** A row's inclusive business-date range, e.g. "1 ต.ค. 69 – 31 ต.ค. 69". */
export const formatDateRange = (
  validFrom: string | null,
  validTo: string | null,
): string => {
  const from = formatThaiShortDate(validFrom, { weekday: false }) || "-";
  const to = validTo
    ? formatThaiShortDate(validTo, { weekday: false })
    : "ไม่มีวันสิ้นสุด";
  return `${from} – ${to}`;
};
